/**
 * MenuBuilderIntroModal — the first-time explanation of the menu builder.
 *
 * Shown to a coach when they open the builder, until they dismiss it for good:
 *   - visits 1 and 2: only "הבנתי" closes it (no ✕, no overlay click, no Esc)
 *   - visit 3 and on: "הבנתי" plus a "אל תציג שוב" button that persists
 *
 * The view counter and the dismissal live on the therapist document
 * (`menuBuilderIntroSeenCount`, `menuBuilderIntroDismissed`) so they follow the
 * coach across devices, AND in localStorage — the auth slice's therapistProfile
 * is not refetched after an update, so the local mirror is what keeps the
 * count correct within one session (same pattern as WelcomeHelpModal).
 *
 * Content-only otherwise: it explains the tree (בלוק → תת-בלוק → מזון → חלופה),
 * the three totals tiers, sum vs. averages, templates and the header actions.
 * Nothing here computes or mutates menu data.
 *
 * Error code: CK-10180 (persist the intro state).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  ArrowsClockwise,
  BookmarkSimple,
  BowlFood,
  ChatText,
  Check,
  FloppyDisk,
  ForkKnife,
  Hamburger,
  Info,
  ListBullets,
  NotePencil,
  Scales,
  SquaresFour,
  Star,
  TreeStructure,
  Users,
} from "@phosphor-icons/react";

import { selectTherapistId, selectTherapistProfile } from "../../../store/slices/authSlice";
import { useUpdateTherapistMutation } from "../../../store/services/therapistApi";
import "./MenuBuilderIntroModal.css";

/** Views on which "הבנתי" is the ONLY way out. From the next one on, the
 *  coach may also dismiss it for good. */
const REQUIRED_VIEWS = 2;

/**
 * Saving a NEW menu replaces the route (…/menu → …/menu/:id), which remounts
 * this page. Without a cooldown the intro would pop straight back up as
 * "view 2" the moment the coach saved their first menu.
 */
const COOLDOWN_MS = 5 * 60 * 1000;

const localKey = (therapistId) => `clinikit_menu_intro_${therapistId}`;

const readLocal = (therapistId) => {
  try {
    const raw = localStorage.getItem(localKey(therapistId));
    const parsed = raw ? JSON.parse(raw) : null;
    return {
      count: Number(parsed?.count) || 0,
      dismissed: parsed?.dismissed === true,
      lastShownAt: Number(parsed?.lastShownAt) || 0,
    };
  } catch {
    return { count: 0, dismissed: false, lastShownAt: 0 };
  }
};

const writeLocal = (therapistId, state) => {
  try {
    localStorage.setItem(localKey(therapistId), JSON.stringify(state));
  } catch {
    /* private mode / quota — the therapist document still holds the state */
  }
};

// ============================================================================
// Small presentational pieces (mock-ups of what the coach will see)
// ============================================================================

const MacroChips = ({ values, mint }) => (
  <span className="nmbi-chips">
    <span className={`nmbi-chip ${mint ? "mint" : ""}`}>קלוריות {values[0]}</span>
    <span className={`nmbi-chip ${mint ? "mint" : ""}`}>פחמימות {values[1]}</span>
    <span className={`nmbi-chip ${mint ? "mint" : ""}`}>חלבון {values[2]}</span>
    <span className={`nmbi-chip ${mint ? "mint" : ""}`}>שומן {values[3]}</span>
  </span>
);

/** A miniature of the block → sub-block → food tree, exactly as the builder draws it. */
const TreeExample = ({ blockName, subBlocks }) => (
  <div className="nmbi-mock-block">
    <div className="nmbi-mock-block-head">
      <span className="nmbi-mock-block-name">{blockName}</span>
      <span className="nmbi-mock-level">בלוק</span>
    </div>
    {subBlocks.map((subBlock) => (
      <div className="nmbi-mock-sub" key={subBlock.name}>
        <div className="nmbi-mock-sub-head">
          <span className="nmbi-mock-sub-name">{subBlock.name}</span>
          <span className="nmbi-mock-level">תת-בלוק</span>
        </div>
        {subBlock.foods.map((food) => (
          <div className="nmbi-mock-food" key={food.name}>
            <span className="nmbi-mock-food-name">{food.name}</span>
            <span className="nmbi-mock-food-amount">{food.amount}</span>
            {food.alt && (
              <span className="nmbi-mock-alt">
                1 <small>מתוך</small> {food.alt}
              </span>
            )}
          </div>
        ))}
      </div>
    ))}
  </div>
);

const Section = ({ icon: Icon, tone, title, children }) => (
  <section className="nmbi-section">
    <header className="nmbi-section-head">
      <span className={`nmbi-section-icon ${tone || ""}`}>
        <Icon size={16} weight="fill" />
      </span>
      <h4>{title}</h4>
    </header>
    <div className="nmbi-section-body">{children}</div>
  </section>
);

// ============================================================================
// Component
// ============================================================================

const MenuBuilderIntroModal = () => {
  const therapistId = useSelector(selectTherapistId);
  const therapistProfile = useSelector(selectTherapistProfile);
  const [updateTherapist] = useUpdateTherapistMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [viewNumber, setViewNumber] = useState(0);
  const decidedRef = useRef(false);

  // Decide ONCE per mount whether to show, and count the view immediately —
  // a coach who leaves the page without clicking still "saw" it.
  useEffect(() => {
    if (decidedRef.current || !therapistId || !therapistProfile) return;
    decidedRef.current = true;

    const local = readLocal(therapistId);
    const remoteCount = Number(therapistProfile.menuBuilderIntroSeenCount) || 0;
    const dismissed =
      local.dismissed || therapistProfile.menuBuilderIntroDismissed === true;
    if (dismissed) return;
    if (Date.now() - local.lastShownAt < COOLDOWN_MS) return;

    const nextCount = Math.max(local.count, remoteCount) + 1;
    setViewNumber(nextCount);
    setIsOpen(true);

    writeLocal(therapistId, { count: nextCount, dismissed: false, lastShownAt: Date.now() });
    updateTherapist({
      uid: therapistId,
      data: { menuBuilderIntroSeenCount: nextCount },
    })
      .unwrap()
      .catch((error) => {
        console.error("[CK-10180] Error saving the menu-builder intro state:", {
          errorCode: "CK-10180",
          errorMessage: error?.message || error?.data?.message || "unknown",
          therapistId,
          timestamp: new Date().toISOString(),
        });
      });
  }, [therapistId, therapistProfile, updateTherapist]);

  // Lock the page scroll while the intro is up
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const handleOk = useCallback(() => setIsOpen(false), []);

  const handleDismissForever = useCallback(() => {
    setIsOpen(false);
    if (!therapistId) return;
    writeLocal(therapistId, { count: viewNumber, dismissed: true, lastShownAt: Date.now() });
    updateTherapist({
      uid: therapistId,
      data: { menuBuilderIntroDismissed: true },
    })
      .unwrap()
      .catch((error) => {
        console.error("[CK-10180] Error saving the menu-builder intro state:", {
          errorCode: "CK-10180",
          errorMessage: error?.message || error?.data?.message || "unknown",
          therapistId,
          timestamp: new Date().toISOString(),
        });
      });
  }, [therapistId, viewNumber, updateTherapist]);

  if (!isOpen) return null;

  const canDismissForever = viewNumber > REQUIRED_VIEWS;
  const remainingViews = Math.max(REQUIRED_VIEWS + 1 - viewNumber, 1);

  const modal = (
    <div className="nmbi-overlay" role="presentation">
      <div
        className="nmbi-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nmbi-title"
      >
        {/* ───── Header ───── */}
        <header className="nmbi-head">
          <span className="nmbi-head-icon">
            <ForkKnife size={22} weight="fill" />
          </span>
          <div className="nmbi-head-text">
            <span className="nmbi-eyebrow">ברוכים הבאים לבונה התפריטים</span>
            <h3 id="nmbi-title">איך בונים כאן תפריט, ואיך הוא מתנהג</h3>
          </div>
          {viewNumber > 0 && (
            <span className="nmbi-view-badge">
              הסבר {Math.min(viewNumber, REQUIRED_VIEWS + 1)} מתוך {REQUIRED_VIEWS + 1}
            </span>
          )}
        </header>

        {/* ───── Body ───── */}
        <div className="nmbi-body">
          <p className="nmbi-lead">
            הבונה הזה גמיש בכוונה: הוא לא מכתיב לכם איך תפריט צריך להיראות. אתם
            קובעים את המבנה, את השמות ואת שיטת החישוב, והמערכת מחשבת עבורכם את
            הערכים התזונתיים בזמן אמת, בכל רמה של התפריט. ההסבר הבא לוקח כמה
            דקות, ואחריו תרגישו בבית.
          </p>

          {/* 1 — the tree */}
          <Section icon={TreeStructure} title="1. המבנה: תפריט ← בלוקים ← תת-בלוקים ← מזונות">
            <p>
              כל תפריט בנוי כעץ בעל שלוש רמות. <strong>בלוק</strong> הוא הקבוצה הגדולה,{" "}
              <strong>תת-בלוק</strong> הוא חלוקה בתוך הבלוק, ובתוך כל תת-בלוק יושבים{" "}
              <strong>המזונות</strong> עצמם עם כמות ויחידת מידה. למזון אפשר לצרף{" "}
              <strong>חלופות</strong> (מזון חלופי שהלקוח/ה יכול/ה להחליף בו).
            </p>
            <TreeExample
              blockName="ארוחת בוקר"
              subBlocks={[
                {
                  name: "אפשרות 1",
                  foods: [
                    { name: "לחם מחיטה מלאה", amount: "2 פרוסות" },
                    { name: "גבינה לבנה 5%", amount: "3 כף", alt: 3 },
                    { name: "עגבנייה", amount: "1 יחידה בינונית" },
                  ],
                },
                {
                  name: "אפשרות 2",
                  foods: [
                    { name: "יוגורט 3%", amount: "1 כוס" },
                    { name: "גרנולה", amount: "30 גרם", alt: 2 },
                  ],
                },
              ]}
            />
            <p className="nmbi-note">
              <Info size={13} weight="fill" />
              שמות הבלוקים והתת-בלוקים הם חופשיים ולא חובה. בלוק ללא שם יופיע אצל
              הלקוח/ה בלי כותרת. הלקוח/ה לעולם לא רואה את המילים "בלוק" או
              "תת-בלוק", רק את השמות שכתבתם.
            </p>
          </Section>

          {/* 2 — three ways to organise */}
          <Section icon={SquaresFour} tone="purple" title="2. הבלוקים הם שלכם: שלוש דרכים נפוצות לארגן תפריט">
            <p>
              מכיוון שאתם בוחרים את שם הבלוק ואת המשמעות שלו, אותו בונה משרת
              סגנונות עבודה שונים. אלה שלוש הדרכים שהמטפלים/ות משתמשים בהן הכי
              הרבה:
            </p>
            <div className="nmbi-styles">
              <div className="nmbi-style">
                <span className="nmbi-style-title">לפי ארוחות היום</span>
                <span className="nmbi-style-tree">
                  בלוק "ארוחת בוקר" · בלוק "ארוחת ביניים" · בלוק "צהריים" · בלוק "ערב"
                </span>
                <span className="nmbi-style-desc">
                  בכל בלוק תת-בלוק אחד עם הארוחה, או כמה תת-בלוקים כאפשרויות
                  חלופיות לאותה ארוחה. הסגנון המוכר ביותר ללקוחות.
                </span>
              </div>
              <div className="nmbi-style">
                <span className="nmbi-style-title">לפי בלוקים של אפשרויות</span>
                <span className="nmbi-style-tree">
                  בלוק "אפשרויות לארוחה חלבונית" ← תת-בלוקים: אפשרות 1, 2, 3...
                </span>
                <span className="nmbi-style-desc">
                  הלקוח/ה בוחר/ת מכל בלוק תת-בלוק אחד בכל פעם. כאן חשוב להפעיל{" "}
                  <strong>תפריט ממוצעים</strong> (סעיף 5) כדי שהסיכום היומי ישקף
                  יום אמיתי ולא את כל האפשרויות יחד.
                </span>
              </div>
              <div className="nmbi-style">
                <span className="nmbi-style-title">לפי קבוצות מזון / רכיבים תזונתיים</span>
                <span className="nmbi-style-tree">
                  בלוק "חלבונים" · בלוק "פחמימות" · בלוק "שומנים" · בלוק "ירקות"
                </span>
                <span className="nmbi-style-desc">
                  מתאים לשיטת "מנות החלפה": בכל בלוק רשימת מזונות עם חלופות, והלקוח/ה
                  מרכיב/ה לבד את הארוחות מתוך הקבוצות.
                </span>
              </div>
            </div>
            <p>
              אפשר גם לערבב, למשל בלוקים לפי ארוחות, ובתוך "צהריים" תת-בלוקים
              בשם "חלבון", "פחמימה", "ירק". אין דרך נכונה אחת.
            </p>
          </Section>

          {/* 3 — foods */}
          <Section icon={BowlFood} tone="mint" title="3. הוספת מזונות, כמויות ויחידות מידה">
            <ul className="nmbi-list">
              <li>
                <strong>הכפתור <Hamburger size={12} weight="fill" className="nmbi-inline-icon mint" /> "הוסף מזון"</strong>{" "}
                בכל תת-בלוק פותח את מאגר המזונות. מקלידים את תחילת השם, אפשר לסנן
                לפי טווחי קלוריות וחלבון ולמיין לפי כל עמודה. לחיצה על שורה מוסיפה
                את המזון מיד, בלי שלב אישור, כדי שתוכלו להוסיף הרבה פריטים ברצף.
              </li>
              <li>
                <strong>הערכים במאגר הם ל-100 גרם.</strong> אחרי ההוספה מזינים{" "}
                <strong>כמות</strong> ובוחרים <strong>יחידת מידה</strong>: גרם, כוס,
                חופן, יחידה קטנה / בינונית / גדולה, כף או כפית (רק היחידות שמוגדרות
                לאותו מזון מוצגות). המערכת ממירה לגרמים ומחשבת את הערכים לפריט.
              </li>
              <li>
                <strong>כמות היא חובה.</strong> פריט ללא כמות מסומן ב-
                <Info size={12} weight="fill" className="nmbi-inline-icon warn" /> ולא ניתן
                לשמור את התפריט עד שממלאים אותה או מוחקים את הפריט.
              </li>
              <li>
                <strong>מזון מותאם אישית</strong> (הלשונית השנייה בחלון הבחירה):
                פריט חד-פעמי לתפריט הזה בלבד, למשל "סלט הבית של דנה". שימו לב
                שכאן הערכים הם <strong>למנה אחת</strong>, והכמות היא מספר המנות.
                מזון שתרצו לשימוש חוזר בכל התפריטים מוסיפים בעמוד{" "}
                <strong>מאגר המזונות</strong> בתפריט הצד.
              </li>
              <li>
                <strong>סדר הפריטים</strong> משתנה בחיצים למעלה / למטה, וכך גם סדר
                התת-בלוקים והבלוקים.
              </li>
            </ul>
          </Section>

          {/* 4 — substitutes */}
          <Section icon={ArrowsClockwise} tone="alt" title="4. חלופות: מזון שהלקוח/ה יכול/ה להחליף">
            <p>
              לכל מזון אפשר לצרף מזונות חלופיים בכפתור{" "}
              <Hamburger size={12} weight="fill" className="nmbi-inline-icon alt" /> הכתום
              שבשורת הפריט. הפריט הופך ל"משפחה": המזון המקורי הוא חלופה 1, וכל
              מזון שצירפתם הוא החלופה הבאה. הלקוח/ה רואה בעמוד האישי "1 מתוך 3"
              ויכול/ה לעבור בין החלופות.
            </p>
            <div className="nmbi-mock-food nmbi-mock-food-demo">
              <span className="nmbi-mock-food-name">גבינה לבנה 5%</span>
              <span className="nmbi-mock-alt">
                1 <small>מתוך</small> 3
              </span>
              <span className="nmbi-mock-food-amount">3 כף</span>
              <span className="nmbi-mock-food-hint">
                <ArrowsClockwise size={12} weight="fill" /> חלופה הבאה: קוטג' 5% · טונה במים
              </span>
            </div>
            <ul className="nmbi-list">
              <li>
                בשורת הפריט מוצגת חלופה אחת בכל פעם. כפתור{" "}
                <ArrowsClockwise size={12} weight="fill" className="nmbi-inline-icon alt" />{" "}
                מדפדף לחלופה הבאה, ו"הצגת כל החלופות" פותח את הרשימה המלאה לעריכת
                הכמות של כל חלופה בנפרד.
              </li>
              <li>
                מתחת לפריט יש שתי שורות ערכים: <strong>"ערכים תזונתיים למנה"</strong>{" "}
                של החלופה המוצגת, ו<strong>"ממוצע כל החלופות"</strong>. בסיכומים של
                התת-בלוק, הבלוק והתפריט נספר <strong>הממוצע</strong>, כי הלקוח/ה
                אוכל/ת בדיוק אחת מהחלופות.
              </li>
              <li>
                מחיקת חלופה 1 לא מוחקת את המשפחה: החלופה הבאה עולה להיות הראשית.
                לחלופה אין חלופות משלה.
              </li>
            </ul>
          </Section>

          {/* 5 — totals */}
          <Section icon={Scales} tone="mint" title="5. סיכומי ערכים בכל רמה, ותפריט ממוצעים">
            <p>
              הערכים מתעדכנים עם כל הקשה, בשלוש רמות: פס ירקרק מתחת לכל{" "}
              <strong>תת-בלוק</strong>, פס סיכום בראש כל <strong>בלוק</strong>, ובתחתית
              המסך <strong>הסרגל הקבוע של התפריט כולו</strong> מול היעדים היומיים של
              הלקוח/ה (אם הוגדרו).
            </p>
            <div className="nmbi-mock-strip">
              <span className="nmbi-mock-strip-label">סיכום ערכים לתת-בלוק</span>
              <MacroChips values={[412, 48, 22, 14]} mint />
            </div>

            <div className="nmbi-modes">
              <div className="nmbi-mode">
                <span className="nmbi-mode-title">
                  <ListBullets size={13} weight="fill" /> תפריט רגיל (ברירת המחדל)
                </span>
                <p>
                  <strong>סכום</strong> של כל התת-בלוקים. מתאים כשהתפריט מתאר יום
                  אכילה אחד שלם: בוקר + ביניים + צהריים + ערב.
                </p>
                <p className="nmbi-mode-example">
                  בלוק "ארוחת בוקר" עם תת-בלוק של 400 קק"ל ותת-בלוק של 500 קק"ל{" "}
                  ← סיכום הבלוק: <strong>900</strong>
                </p>
              </div>
              <div className="nmbi-mode alt">
                <span className="nmbi-mode-title">
                  <Scales size={13} weight="fill" /> תפריט ממוצעים
                </span>
                <p>
                  התת-בלוקים הם <strong>אפשרויות חלופיות</strong>, לכן הסיכום הוא{" "}
                  <strong>ממוצע</strong>: הסכום מחולק במספר התת-בלוקים שיש בהם
                  מזונות. מתאים כשהלקוח/ה בוחר/ת אפשרות אחת מכל בלוק.
                </p>
                <p className="nmbi-mode-example">
                  אותו בלוק ← סיכום הבלוק: <strong>450</strong>, ובסרגל התחתון
                  "ממוצע ערכים יומי" במקום "סה"כ ערכים יומיים"
                </p>
              </div>
            </div>
            <p className="nmbi-note">
              <Info size={13} weight="fill" />
              מחליפים מצב בכפתור "פעולות נוספות" ← "תפריט ממוצעים". במצב הזה יופיע
              תג כתום "תפריט ממוצעים" ליד שם התפריט, וכל הסיכומים והכותרות מתעדכנים
              מיד. אפשר להחליף הלוך ושוב בכל רגע.
            </p>
          </Section>

          {/* 6 — templates */}
          <Section icon={BookmarkSimple} tone="alt" title="6. תבניות שמורות: בלוקים, תת-בלוקים ומזונות לשימוש חוזר">
            <p>
              בניתם ארוחת בוקר מוצלחת? שמרו אותה. ליד כל בלוק, תת-בלוק ומזון יש
              כפתור סימנייה{" "}
              <BookmarkSimple size={12} weight="fill" className="nmbi-inline-icon alt" />.
              לחיצה עליו, שם לתבנית, והיא מופיעה בסרגל <strong>"תבניות המרפאה"</strong>{" "}
              שבצד המסך, מחולק לשלושה מדפים: בלוקים, תת-בלוקים ומזונות (מזון נשמר
              יחד עם הכמות והחלופות שלו).
            </p>
            <ol className="nmbi-steps">
              <li>לוחצים "הוסף לתפריט" על התבנית בסרגל הצד.</li>
              <li>
                בתפריט נפתחים אזורי <span className="nmbi-mock-drop">הוסף כאן</span>{" "}
                בכל מקום שבו התבנית יכולה להיכנס (בלוק בין בלוקים, תת-בלוק בתוך
                בלוק, מזון בתוך תת-בלוק).
              </li>
              <li>לוחצים על המקום הרצוי. התבנית מועתקת לשם, ואפשר לערוך אותה בלי להשפיע על התבנית עצמה.</li>
            </ol>
            <p className="nmbi-note">
              <Info size={13} weight="fill" />
              התבניות משותפות לכל המטפלים/ות במרפאה ולכל התפריטים. מחיקת תבנית לא
              משנה תפריטים שכבר נבנו ממנה. יציאה ממצב ההוספה: "ביטול" או מקש Esc.
            </p>
          </Section>

          {/* 7 — notes & comments */}
          <Section icon={ChatText} tone="purple" title="7. הערות: מה הלקוח/ה רואה ומה לא">
            <ul className="nmbi-list">
              <li>
                <strong>הערה כללית לתפריט</strong> (מתחת לשם התפריט) מופיעה בראש
                התפריט של הלקוח/ה, למשל "לשתות 2 ליטר מים ביום".
              </li>
              <li>
                <strong>הערות לבלוק</strong> (כפתור בתחתית כל בלוק) מופיעות ליד
                הבלוק בעמוד האישי וב-PDF, למשל "אפשר להחליף בין הארוחות".
              </li>
              <li>
                <strong>הערות מאמן/ת</strong> (הפאנל בתחתית הבונה) הן פנימיות בלבד,
                לכם ולצוות. הלקוח/ה לעולם לא רואה אותן.
              </li>
            </ul>
          </Section>

          {/* 8 — save / active / actions */}
          <Section icon={FloppyDisk} title="8. שמירה, תפריט פעיל ופעולות נוספות">
            <ul className="nmbi-list">
              <li>
                <strong>שמור</strong> הוא הכפתור היחיד שפועל בלחיצה אחת. עד השמירה
                מופיע תג "שינויים שלא נשמרו", והמערכת תזהיר אם תנסו לצאת.
              </li>
              <li>
                <Star size={12} weight="fill" className="nmbi-inline-icon mint" />{" "}
                <strong>תפריט פעיל:</strong> ללקוח/ה יש תפריט פעיל אחד בלבד, והוא
                היחיד שמופיע בעמוד האישי שלו/ה. התפריט הראשון שאתם שומרים הופך
                לפעיל אוטומטית. תפריטים נוספים שומרים כטיוטה ומפעילים מתוך "פעולות
                נוספות" כשמוכנים. תפריט פעיל אי אפשר למחוק.
              </li>
              <li>
                <strong>שכפול</strong> יוצר עותק לא פעיל, נוח כבסיס לתפריט הבא.{" "}
                <strong>ייצוא PDF</strong> מפיק קובץ ממותג של המרפאה מהגרסה השמורה
                (לכן צריך לשמור קודם).
              </li>
              <li>
                <Users size={12} weight="fill" className="nmbi-inline-icon" />{" "}
                הלקוח/ה מגיע/ה לתפריט דרך <strong>הקישור לעמוד האישי</strong> ששלחתם
                (מתוך כרטיס הלקוח/ה או מרכז הליווי), ולא צריך לשלוח כל תפריט מחדש.
              </li>
            </ul>
          </Section>

          {/* Quick start */}
          <div className="nmbi-quickstart">
            <span className="nmbi-quickstart-title">
              <Check size={14} weight="bold" /> התחלה מהירה, בחמישה צעדים
            </span>
            <ol>
              <li>נותנים שם לתפריט (חובה), למשל "תפריט 1,800 קלוריות".</li>
              <li>מחליטים על שיטת הבלוקים (ארוחות / אפשרויות / קבוצות מזון) ונותנים שמות.</li>
              <li>בכל תת-בלוק מוסיפים מזונות, ממלאים כמות ובוחרים יחידה. מצרפים חלופות למי שצריך.</li>
              <li>אם התת-בלוקים הם אפשרויות חלופיות, מפעילים "תפריט ממוצעים".</li>
              <li>בודקים את הסרגל התחתון מול היעדים, לוחצים שמור, ושומרים כתבנית מה ששווה לשמור.</li>
            </ol>
          </div>

          <p className="nmbi-outro">
            <NotePencil size={13} weight="fill" /> כל כפתור בבונה מסביר את עצמו
            במעבר עכבר (או לחיצה ארוכה בנייד), אז לא צריך לזכור הכול עכשיו.
          </p>
        </div>

        {/* ───── Footer ───── */}
        <footer className="nmbi-foot">
          {canDismissForever ? (
            <button type="button" className="nmbi-btn secondary" onClick={handleDismissForever}>
              אל תציג הודעה זו שוב
            </button>
          ) : (
            <span className="nmbi-foot-hint">
              {remainingViews === 1
                ? "ההסבר יופיע עוד פעם אחת, ואז אפשר יהיה לכבות אותו"
                : `ההסבר יופיע עוד ${remainingViews} פעמים, ואז אפשר יהיה לכבות אותו`}
            </span>
          )}
          <button type="button" className="nmbi-btn primary" onClick={handleOk}>
            <Check size={15} weight="bold" />
            <span>הבנתי, בואו נתחיל</span>
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default MenuBuilderIntroModal;
